import { useState, useEffect, Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './auth/AuthProvider';
import { useRBAC } from './auth/RBACProvider';
import Sidebar from './components/Sidebar';
import ClientsPage from './pages/clients/ClientsPage';
// import DebugPanel from './components/DebugPanel'; // Uncomment when debugging needed
import { useFeatureFlags } from './hooks/useFeatureFlags';
import ErrorDisplay from './components/shared/ErrorDisplay';
import LoadingSpinner from './components/shared/LoadingSpinner';
import UploadNotificationBell from './components/notifications/UploadNotificationBell';
import { UploadJobsProvider } from './context/UploadJobsContext';
import { isEmailLinkSignIn } from './auth/firebase';
import GuestSignIn from './components/auth/GuestSignIn';
import PendingApprovalPage from './components/auth/PendingApprovalPage';
import EmailLinkCallback from './components/auth/EmailLinkCallback';
import { clearPostSignInRedirectPath, setPostSignInRedirectPath } from './auth/firebase';
import logger from './utils/logger';
// Import test script in development mode
// Removed testApi dev harness to ensure real auth token is used
import './styles.css';

// Lazy load heavy components for code splitting
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Prospects = lazy(() => import('./pages/Prospects'));
const Leads = lazy(() => import('./pages/Leads'));
const EmailCampaigns = lazy(() => import('./pages/EmailCampaigns'));
const GoogleAnalytics = lazy(() => import('./pages/GoogleAnalytics'));
const GoogleAds = lazy(() => import('./pages/GoogleAds'));
const LinkedIn = lazy(() => import('./pages/LinkedIn'));
const UserManagement = lazy(() => import('./pages/users/UserManagementPage'));
const AccountManagement = lazy(() => import('./pages/accounts/AccountManagementPage'));
const SystemMonitoring = lazy(() => import('./pages/SystemMonitoringPage'));

type AppPageId =
  | 'dashboard'
  | 'prospects'
  | 'email-campaigns'
  | 'linkedin-campaigns'
  | 'google-analytics'
  | 'google-ads'
  | 'meta-campaigns'
  | 'leads'
  | 'client-config'
  | 'user-management'
  | 'account-management'
  | 'system-monitoring';

const pagePathById: Record<AppPageId, string> = {
  dashboard: '/dashboard',
  prospects: '/prospects',
  'email-campaigns': '/email-campaigns',
  'linkedin-campaigns': '/linkedin-campaigns',
  'google-analytics': '/google-analytics',
  'google-ads': '/google-ads',
  'meta-campaigns': '/meta-campaigns',
  leads: '/leads',
  'client-config': '/client-config',
  'user-management': '/user-management',
  'account-management': '/account-management',
  'system-monitoring': '/system-monitoring',
};

const orderedProtectedPages: AppPageId[] = [
  'dashboard',
  'email-campaigns',
  'linkedin-campaigns',
  'google-analytics',
  'google-ads',
  'meta-campaigns',
  'leads',
  'prospects',
  'client-config',
  'user-management',
  'account-management',
  'system-monitoring',
];

const normalizePathToPageId = (pathname: string): AppPageId => {
  const normalized = pathname.replace(/\/+$/, '') || '/dashboard';
  const match = Object.entries(pagePathById).find(([, path]) => path === normalized);
  return (match?.[0] as AppPageId | undefined) || 'dashboard';
};

// Under construction component for non-active pages
const UnderConstruction = ({ title, features }: { title: string; features: string[] }) => (
  <div className="page-content">
    <h1 className="page-title">{title}</h1>
    <div className="under-construction">
      <div className="construction-icon">🚧</div>
      <h2>Under Construction</h2>
      <p>This feature is currently being developed and will include:</p>
      <ul style={{ textAlign: 'left', maxWidth: '600px', margin: '0 auto' }}>
        {features.map((feature, index) => (
          <li key={index}>{feature}</li>
        ))}
      </ul>
      <p style={{ marginTop: '2rem', fontStyle: 'italic' }}>
        Expected completion: Phase 2
      </p>
    </div>
  </div>
);

function InnerApp() {
  const { user, loading, error, signIn, signOut } = useAuth();
  const { hasPageAccess, loading: rbacLoading, userProfile: rbacUserProfile } = useRBAC();
  const { loading: flagsLoading, error: flagsError, isEnabled } = useFeatureFlags();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loginMode, setLoginMode] = useState<'initial' | 'guest'>('initial');
  const [userProfile, setUserProfile] = useState<{ role?: string } | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const currentPage = normalizePathToPageId(location.pathname);
  const isEmailCallbackRoute = location.pathname === '/auth/email-link-callback' || isEmailLinkSignIn();

  useEffect(() => {
    if (!user && !isEmailCallbackRoute) {
      const intendedPath = `${location.pathname}${location.search}${location.hash}`;
      setPostSignInRedirectPath(intendedPath === '/' ? '/dashboard' : intendedPath);
    }
  }, [user, isEmailCallbackRoute, location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (user && !isEmailCallbackRoute) {
      clearPostSignInRedirectPath();
    }
  }, [user, isEmailCallbackRoute]);

  // Fetch user profile when authenticated to check for PENDING status
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (user) {
        try {
          const response = await fetch('/api/users/me', {
            headers: {
              'Authorization': `Bearer ${await user.getIdToken()}`
            }
          });
          if (response.ok) {
            const profile = await response.json();
            setUserProfile(profile);
          } else if (response.status === 404) {
            // User is authenticated via Firebase but not yet in our system
            // This means they're a new user awaiting initialization/approval
            logger.log('User not found in system - treating as pending initialization');
            setUserProfile({ role: 'pending', _uninitialized: true });
          } else if (response.status === 403) {
            // User exists but is PENDING approval
            const errorData = await response.json().catch(() => ({}));
            if (errorData.status === 'pending_approval' || errorData.role === 'pending') {
              setUserProfile({ role: 'pending' });
            } else {
              logger.error('User access forbidden');
            }
          }
        } catch (err) {
          logger.error('Failed to fetch user profile');
        }
      } else {
        setUserProfile(null);
      }
    };
    fetchUserProfile();
  }, [user]);

  // Listen for navigate events from notification bell
  useEffect(() => {
    const handleNavigateEvent = (e: CustomEvent<string>) => {
      const targetPath = pagePathById[(e.detail as AppPageId)] || '/dashboard';
      navigate(targetPath);
    };
    window.addEventListener('navigate', handleNavigateEvent as EventListener);
    return () => window.removeEventListener('navigate', handleNavigateEvent as EventListener);
  }, [navigate]);

  const handleNavigate = (page: string) => {
    const targetPath = pagePathById[(page as AppPageId)] || '/dashboard';
    navigate(targetPath);
  };

  const getFirstAccessiblePath = (): string => {
    const accessiblePage = orderedProtectedPages.find((page) => hasPageAccess(page));
    return accessiblePage ? pagePathById[accessiblePage] : '/dashboard';
  };

  const renderCurrentPage = (page: AppPageId) => {
    // Only show loading spinner for feature flags if user is authenticated
    if (user && flagsLoading) {
      return <LoadingSpinner message="Loading application..." fullScreen />;
    }

    switch (page) {
      case 'dashboard':
        return null;
      case 'prospects':
        return (
          <Suspense fallback={<LoadingSpinner message="Loading Prospects..." />}>
            <Prospects />
          </Suspense>
        );
      case 'email-campaigns':
        if (isEnabled('feature.email.campaigns')) {
          return (
            <Suspense fallback={<LoadingSpinner message="Loading Email Campaigns..." />}>
              <EmailCampaigns />
            </Suspense>
          );
        } else {
          return (
            <UnderConstruction
              title="Email Campaigns"
              features={[
                '📧 Campaign summary reports by client and date range',
                '📊 Integration with MailerCloud API for real-time data',
                '🎯 Campaign details with prospect-level lead scoring',
                '📈 Performance metrics and analytics',
                '⏰ Automated lead scoring based on email events'
              ]}
            />
          );
        }
      case 'linkedin-campaigns':
        if (isEnabled('feature.linkedin.campaigns')) {
          return (
            <Suspense fallback={<LoadingSpinner message="Loading LinkedIn Campaigns..." />}>
              <LinkedIn />
            </Suspense>
          );
        } else {
          return (
            <UnderConstruction
              title="LinkedIn Campaigns"
              features={[
                '💼 Ad campaign performance metrics (impressions, costs)',
                '🎨 Ad creative performance tracking',
                '📱 Organic post analytics',
                '📊 Time period and client filtering',
                '🔄 Integration with LinkedIn Marketing API'
              ]}
            />
          );
        }
      case 'google-analytics':
        if (isEnabled('feature.google.campaigns')) {
          return (
            <Suspense fallback={<LoadingSpinner message="Loading Google Analytics..." />}>
              <GoogleAnalytics />
            </Suspense>
          );
        } else {
          return (
            <UnderConstruction
              title="Google Analytics & Ads Campaigns"
              features={[
                '🎯 GA4 Analytics integration with real-time metrics',
                '💰 Google Ads campaign performance tracking',
                '📈 Interactive charts and data visualizations',
                '📊 Multi-account support and filtering',
                '🔄 Data synchronization with Google APIs',
                '📅 Custom date range filtering',
                '📋 Comprehensive data tables with pagination'
              ]}
            />
          );
        }
      case 'google-ads':
        if (isEnabled('feature.google.campaigns')) {
          return (
            <Suspense fallback={<LoadingSpinner message="Loading Google Ads..." />}>
              <GoogleAds />
            </Suspense>
          );
        } else {
          return (
            <UnderConstruction
              title="Google Ads Campaigns"
              features={[
                '🎯 Campaign performance tracking',
                '💰 Cost and conversion metrics',
                '📈 Keyword performance analysis',
                '📊 Client and campaign filtering',
                '🔄 Integration with Google Ads API'
              ]}
            />
          );
        }
      case 'meta-campaigns':
        if (isEnabled('feature.meta.campaigns')) {
          return (
            <Suspense fallback={<LoadingSpinner message="Loading Meta Campaigns..." />}>
              <div>🚧 Meta Campaigns Component (Coming Soon)</div>
            </Suspense>
          );
        } else {
          return (
            <UnderConstruction
              title="Meta Campaigns"
              features={[
                '📱 Facebook and Instagram campaign tracking',
                '💰 Ad spend and performance metrics',
                '🎯 Audience insights and targeting analysis',
                '📊 Cross-platform campaign comparison',
                '🔄 Integration with Meta Business API'
              ]}
            />
          );
        }
      case 'leads':
        return (
          <Suspense fallback={<LoadingSpinner message="Loading Leads..." />}>
            <Leads />
          </Suspense>
        );
      case 'client-config':
        return <ClientsPage />;
      case 'user-management':
        return (
          <Suspense fallback={<LoadingSpinner message="Loading User Management..." />}>
            <UserManagement />
          </Suspense>
        );
      case 'account-management':
        return (
          <Suspense fallback={<LoadingSpinner message="Loading Account Management..." />}>
            <AccountManagement />
          </Suspense>
        );
      case 'system-monitoring':
        return (
          <Suspense fallback={<LoadingSpinner message="Loading System Monitoring..." />}>
            <SystemMonitoring />
          </Suspense>
        );
      default:
        return (
          <Suspense fallback={<LoadingSpinner message="Loading Dashboard..." />}>
            <Dashboard />
          </Suspense>
        );
    }
  };

  const renderProtectedRoute = (page: AppPageId) => {
    const awaitingRbacBootstrap = Boolean(user && !rbacUserProfile && !userProfile);
    if (user && (rbacLoading || awaitingRbacBootstrap)) {
      return <LoadingSpinner message="Loading access permissions..." fullScreen />;
    }
    if (!hasPageAccess(page)) {
      const fallbackPath = getFirstAccessiblePath();
      return fallbackPath === location.pathname
        ? <ErrorDisplay error={{ message: "You don't have access to this page.", type: 'warning' }} onClose={() => {}} showRetry={false} />
        : <Navigate to={fallbackPath} replace />;
    }
    return renderCurrentPage(page);
  };

  return (
    <UploadJobsProvider enabled={!!user}>
      <div className={`app-container dashboard-container ${user ? 'compact' : ''}`}>
        {loading ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            flexDirection: 'column'
          }}>
            <h2>Loading...</h2>
            <p>Initializing authentication...</p>
          </div>
        ) : error ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            flexDirection: 'column',
            color: 'red'
          }}>
            <h2>Error</h2>
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>Reload Page</button>
          </div>
        ) : isEmailCallbackRoute ? (
          // Email link callback processing
          <EmailLinkCallback 
            onSuccess={(targetPath) => navigate(targetPath || '/dashboard', { replace: true })}
            onError={(targetPath) => navigate(targetPath || '/dashboard', { replace: true })}
          />
        ) : !user ? (
          // Login page
          <>
            <div className="dashboard-header centered">
              <img 
                src="/wizrdom-logo.png" 
                alt="Wizrdom Logo" 
                className="logo"
              />
              <h1 className="dashboard-title">Metrics Dashboard</h1>
              
              {loginMode === 'initial' ? (
                <div className="login-buttons-container">
                  <button className="wizrdom-button home-primary" onClick={signIn}>
                    <span className="elementor-button-content-wrapper">
                      <span className="elementor-button-text">Employee Sign In</span>
                    </span>
                  </button>
                  <button 
                    className="wizrdom-button home-secondary" 
                    onClick={() => setLoginMode('guest')}
                  >
                    <span className="elementor-button-content-wrapper">
                      <span className="elementor-button-text">Guest / Partner Access</span>
                    </span>
                  </button>
                </div>
              ) : (
                <div className="guest-login-container">
                  <GuestSignIn onBack={() => setLoginMode('initial')} />
                </div>
              )}
            </div>
          </>
        ) : userProfile?.role === 'pending' ? (
          // User is pending approval
          <PendingApprovalPage 
            userEmail={user?.email}
            onLogout={signOut} 
            getIdToken={() => user.getIdToken()}
          />
        ) : (
          // Dashboard with sidebar navigation
          <>
            {/* <DebugPanel /> */}
            <Sidebar 
              isOpen={sidebarOpen} 
              onClose={() => setSidebarOpen(false)}
              onNavigate={handleNavigate}
              currentPage={currentPage}
            />
            
            <div className="dashboard-header compact">
              <button 
                className="menu-button"
                onClick={() => setSidebarOpen(true)}
                aria-label="Open menu"
              >
                ☰
              </button>
              
              <img 
                src="/wizrdom-logo.png" 
                alt="Wizrdom Logo" 
                className="logo small"
              />
              <h1 className="dashboard-title small">Metrics Dashboard</h1>
              
              <div className="top-nav">
                <UploadNotificationBell />
                <span style={{ color: 'white', marginRight: '1rem', fontSize: '0.9rem' }}>
                  {user.email || user.displayName}
                </span>
                <button className="wizrdom-button small" onClick={() => { signOut(); navigate('/dashboard', { replace: true }); }}>
                  <span className="elementor-button-content-wrapper">
                    <span className="elementor-button-text">Logout</span>
                  </span>
                </button>
              </div>
            </div>
            
            <div style={{ width: '100%', marginTop: '1rem' }}>
              {flagsError && (
                <ErrorDisplay
                  error={{
                    message: flagsError,
                    type: 'warning'
                  }}
                  onClose={() => {}}
                  showRetry={false}
                />
              )}
              {user && !rbacLoading && hasPageAccess('dashboard') && (
                <div style={{ display: location.pathname === '/dashboard' ? 'block' : 'none' }}>
                  <Suspense fallback={<LoadingSpinner message="Loading Dashboard..." />}>
                    <Dashboard isActive={location.pathname === '/dashboard'} />
                  </Suspense>
                </div>
              )}
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={renderProtectedRoute('dashboard')} />
                <Route path="/prospects" element={renderProtectedRoute('prospects')} />
                <Route path="/email-campaigns" element={renderProtectedRoute('email-campaigns')} />
                <Route path="/linkedin-campaigns" element={renderProtectedRoute('linkedin-campaigns')} />
                <Route path="/google-analytics" element={renderProtectedRoute('google-analytics')} />
                <Route path="/google-ads" element={renderProtectedRoute('google-ads')} />
                <Route path="/meta-campaigns" element={renderProtectedRoute('meta-campaigns')} />
                <Route path="/leads" element={renderProtectedRoute('leads')} />
                <Route path="/client-config" element={renderProtectedRoute('client-config')} />
                <Route path="/user-management" element={renderProtectedRoute('user-management')} />
                <Route path="/account-management" element={renderProtectedRoute('account-management')} />
                <Route path="/system-monitoring" element={renderProtectedRoute('system-monitoring')} />
                <Route
                  path="/auth/email-link-callback"
                  element={
                    <EmailLinkCallback
                      onSuccess={(targetPath) => navigate(targetPath || '/dashboard', { replace: true })}
                      onError={(targetPath) => navigate(targetPath || '/dashboard', { replace: true })}
                    />
                  }
                />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </div>
          </>
          )}
      </div>
    </UploadJobsProvider>
  );
}

export default function App() {
  return <InnerApp />;
}
